import { useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';

import { createColumnHelper } from '@tanstack/react-table';
import {
  ArrowPathIcon,
  ExclamationCircleIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { Card } from '../../components/base/Card';
import { DataTable } from '../../components/base/Table';
import { Button } from '../../components/base/Button';
import { useUserContext } from '../../contexts/UserContext';
import { planetDeviceQueryKeys } from '../../data/QueryKeys';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { EditDevice } from '../../components/Device/EditDevice';
import { useClipboardCopy } from '../../hooks/useClipboardCopy';
import { SubscriptionTier } from '../../types/Billing/Subscription';
import { useApplianceLookups } from '../../hooks/useApplianceLookups';
import { ConfirmDelete } from '../../components/shared/ConfirmDelete';
import { SectionLoading } from '../../components/shared/SectionLoading';
import usePlanetDeviceMutations from '../../data/planetDevice/usePlanetDeviceMutations';
import {
  IPlanetDeviceWithFingotiDevice,
  PartNumberModel,
} from '../../types/PlanetDevice/PlanetDevice';

const colHelper = createColumnHelper<IPlanetDeviceWithFingotiDevice>();

export function DevicesPage() {
  const queryClient = useQueryClient();
  const { setPageTitle } = usePageTitle();
  const { delPlanetDevice, setupPlanetDevice } = usePlanetDeviceMutations();
  const [editOpen, setEditOpen] = useState(false);
  const [editDevice, setEditDevice] = useState<
    IPlanetDeviceWithFingotiDevice | undefined
  >(undefined);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteDevice, setDeleteDevice] = useState<
    IPlanetDeviceWithFingotiDevice | undefined
  >(undefined);
  const { isLoading } = useApplianceLookups();
  const { CopyToClipboardButton } = useClipboardCopy();
  const { isSuperAdmin } = useUserContext();

  const handleEditDevice = (device: IPlanetDeviceWithFingotiDevice) => {
    setEditDevice(device);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    editDevice && setTimeout(() => setEditDevice(undefined), 200);
    setEditOpen(false);
  };

  const handleDeleteDevice = (device: IPlanetDeviceWithFingotiDevice) => {
    setDeleteDevice(device);
    setDeleteOpen(true);
  };

  const handleConfirmDeleteDevice = () => {
    delPlanetDevice.mutate({
      ...deleteDevice!,
    });
    setDeleteOpen(false);
  };

  const handleSetupDevice = (device: IPlanetDeviceWithFingotiDevice) =>
    setupPlanetDevice.mutate({
      devices: [device!.id],
    });

  const refetchDevices = () => {
    queryClient.refetchQueries(planetDeviceQueryKeys.allWithGateway());
  };

  useEffect(() => {
    setPageTitle('Manage Devices');
  }, [setPageTitle]);

  const cols = [
    colHelper.accessor('serialNumber', {
      header: 'Serial No',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          <span>{info.getValue()}</span>
          {info.row.original.setupRequired && (
            <ExclamationCircleIcon className="w-5 h-5 text-amber-500" />
          )}
        </div>
      ),
    }),
    colHelper.display({
      cell: (info) =>
        PartNumberModel[
          info.row.original.partNumber as keyof typeof PartNumberModel
        ],
      header: 'Model',
    }),
    colHelper.accessor(
      (row) => Object.values(SubscriptionTier)[row.billing.tier],
      {
        header: 'Subscription',
      }
    ),
    colHelper.accessor('gateway.device.cloud.connected', {
      header: 'Status',
      cell: (info) => (
        <div className="flex items-center space-x-2">
          {info.getValue() ? (
            <>
              <div className="w-5 h-5 bg-green-600 rounded-full" />
              <span>Online</span>
            </>
          ) : (
            <>
              <div className="w-5 h-5 bg-red-600 rounded-full" />
              <span>Offline</span>
            </>
          )}
        </div>
      ),
    }),
    colHelper.display({
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          <PencilIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => handleEditDevice(info.row.original)}
          />
          {!info.row.original.gateway?.device?.cloud?.connected && (
            <TrashIcon
              className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
              onClick={() => handleDeleteDevice(info.row.original)}
            />
          )}
          {isSuperAdmin ? (
            <>
              {info.row.original.gateway?.device?.cloud?.connected && (
                <ArrowPathIcon
                  className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                  onClick={() => handleSetupDevice(info.row.original)}
                />
              )}
              <CopyToClipboardButton iconOnly copyText={info.row.original.id} />
            </>
          ) : (
            <>
              {info.row.original.gateway?.device?.cloud?.connected &&
                info.row.original.setupRequired && (
                  <ArrowPathIcon
                    className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                    onClick={() => handleSetupDevice(info.row.original)}
                  />
                )}
            </>
          )}
        </div>
      ),
    }),
  ];

  return (
    <>
      <EditDevice
        open={editOpen}
        device={editDevice}
        onClose={handleEditClose}
      />
      <ConfirmDelete
        itemName={deleteDevice?.serialNumber}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => handleConfirmDeleteDevice()}
      />
      <Card title="All Devices">
        {isLoading ? (
          <SectionLoading />
        ) : (
          <div>
            <div className="my-6">
              <Button onClick={refetchDevices}>Refresh List</Button>
            </div>
            <DataTable<IPlanetDeviceWithFingotiDevice>
              url="/device"
              queryKey={planetDeviceQueryKeys.allWithGateway()}
              columns={cols}
              queryString={{
                includeGateway: true,
              }}
            />
          </div>
        )}
      </Card>
    </>
  );
}
